import { useEffect, useRef, useState } from "react";
import { Button, Form, Grid, Input, Modal } from 'semantic-ui-react';
import '../styles/Login.scss';
import { useNavigate } from 'react-router-dom'
import { accountService } from '../services';
import { RequestPayload } from '../services/ApiClient/types';
import { AuthenticateRequest, MfaVerifyRequest } from "../services/AccountService/types";
import { useUser } from '../components/AppProvider';
import { User } from '../services/types';
import { SSOLoginModal } from '../components/SSOLoginModal';
import { getConfig } from '../config';
import LeafletMap from "../components/LeafletMap";

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: object) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '';

const Login = () => {
  const navigate = useNavigate();
  const { setUser } = useUser();
  const config = getConfig();

  const [isLoginError, setIsLoginError] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string>('Error');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSSOModalOpen, setIsSSOModalOpen] = useState<boolean>(false);

  // MFA step
  const [mfaRequired, setMfaRequired] = useState<boolean>(false);
  const [mfaChallengeToken, setMfaChallengeToken] = useState<string>('');
  const [mfaCode, setMfaCode] = useState<string>('');
  const [mfaUsername, setMfaUsername] = useState<string>('');

  // Turnstile
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string>('');
  const turnstileToken = useRef<string>('');

  const loginRef = useRef({ userName: '', password: '' });

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    const scriptId = 'cf-turnstile-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const tryRender = () => {
      if (window.turnstile && turnstileRef.current && !turnstileWidgetId.current) {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => { turnstileToken.current = token; },
          'expired-callback': () => { turnstileToken.current = ''; },
          theme: 'light',
        });
      } else {
        setTimeout(tryRender, 200);
      }
    };
    tryRender();

    return () => {
      if (window.turnstile && turnstileWidgetId.current) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = '';
      }
    };
  }, []);

  const completeLogin = async (response: any) => {
    const token = response.result.jwtToken;
    localStorage.setItem("token", token);
    localStorage.setItem("authenticated", 'true');

    const currentRoute: string = localStorage.getItem("route") ?? "Routed";
    localStorage.setItem("route", currentRoute);

    const user: User = {
      userName: response.result.userPreferences.userName,
      userPreferences: response.result.userPreferences.userPreferences,
      userRestrictions: response.result.userPreferences.userRestrictions,
      applicationFeatures: response.result.userPreferences.applicationFeatures,
      courierId: response.result.courierId,
      admin: response.result.admin,
      email: response.result.email,
    };

    localStorage.removeItem("courierId");
    const courierId = user.courierId ? user.courierId.toString() : null;
    if (courierId === null) localStorage.setItem("route", 'Routed');
    localStorage.setItem("courierId", courierId);
    setUser(user);
    navigate(`/${currentRoute}`);
  };

  const handleTraditionalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const request: AuthenticateRequest = {
      username: loginRef.current.userName,
      password: loginRef.current.password,
      turnstileToken: turnstileToken.current || undefined,
    };

    const payload: RequestPayload<AuthenticateRequest> = { payload: request };

    try {
      const response = await accountService.login(payload);

      if (response.success) {
        if (response.result.mfaRequired) {
          setMfaRequired(true);
          setMfaChallengeToken(response.result.mfaChallengeToken ?? '');
          setMfaUsername(loginRef.current.userName);
          return;
        }

        await completeLogin(response);

        // DriverSync login (fire-and-forget)
        fetch('https://prod.integratewithfetchgoat.com/fgn-ds-login2', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: loginRef.current.userName, password: loginRef.current.password })
        })
          .then(r => r.json())
          .then(data => {
            localStorage.setItem('DSLogin', data.token);
            localStorage.setItem('DSLoginIssuedAt', String(Date.now()));
          })
          .catch(console.error);
      }
    } catch (err: any) {
      if (window.turnstile && turnstileWidgetId.current) {
        window.turnstile.reset(turnstileWidgetId.current);
        turnstileToken.current = '';
      }
      setIsLoginError(true);
      setLoginError(err.data?.message || err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const request: MfaVerifyRequest = {
      mfaChallengeToken: mfaChallengeToken,
      code: mfaCode.trim(),
    };

    try {
      const response = await accountService.verifyMfa({ payload: request });
      if (response.success) {
        await completeLogin(response);
      } else {
        setIsLoginError(true);
        setLoginError(response.message || 'Invalid MFA code.');
      }
    } catch (err: any) {
      setIsLoginError(true);
      setLoginError(err.data?.message || err.message || 'MFA verification failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='login-form'>
      <div className="grid_container">
        <Grid columns="1" style={{ height: '0px' }}>
          <Grid.Row>
            <Grid.Column xs={1} />
            <Grid.Column xs={8} style={{ display: 'flex', justifyContent: 'center' }} className="grid-column">
              <div className='fb-login-container'>
                <div className="fb-item fb-form">
                  <img className="logo" src='/Images/logo.png' alt="Logo" />

                  {!mfaRequired ? (
                    <Form className='form-login' onSubmit={handleTraditionalLogin}>
                      <Form.Field className="username">
                        <input
                          placeholder='Username'
                          id='username'
                          onChange={(e) => { loginRef.current.userName = e.target.value; }}
                        />
                      </Form.Field>
                      <Form.Field className="password">
                        <Input
                          type='password'
                          placeholder='Password'
                          id='password'
                          onChange={(e) => { loginRef.current.password = e.target.value; }}
                        />
                      </Form.Field>
                      {TURNSTILE_SITE_KEY && (
                        <Form.Field>
                          <div ref={turnstileRef} style={{ margin: '8px 0' }} />
                        </Form.Field>
                      )}
                      <div id="login-buttons">
                        <Button disabled={isLoading} loading={isLoading} className="login" type='submit' color="blue">
                          {!isLoading ? 'Log In' : 'Loading...'}
                        </Button>
                        {config.featureFlags.ssoLogin && (
                          <Button
                            className="sso-login"
                            onClick={() => setIsSSOModalOpen(true)}
                            color="green"
                            type="button"
                          >
                            Sign in with SSO
                          </Button>
                        )}
                        <Button className="forgotPass" type="button" onClick={() => navigate('forgotpassword')}>
                          Forgot Password
                        </Button>
                      </div>
                    </Form>
                  ) : (
                    <Form className='form-login' onSubmit={handleMfaVerify}>
                      <p style={{ marginBottom: '12px', color: '#555' }}>
                        Enter the 6-digit code from your authenticator app for <strong>{mfaUsername}</strong>.
                      </p>
                      <Form.Field className="verification">
                        <Input
                          placeholder='Authentication code'
                          value={mfaCode}
                          onChange={(e) => setMfaCode(e.target.value)}
                          maxLength={20}
                          autoFocus
                        />
                      </Form.Field>
                      <div id="login-buttons">
                        <Button disabled={isLoading} loading={isLoading} className="login" type='submit' color="blue">
                          {!isLoading ? 'Verify' : 'Verifying...'}
                        </Button>
                        <Button type="button" onClick={() => { setMfaRequired(false); setMfaCode(''); }}>
                          Back
                        </Button>
                      </div>
                    </Form>
                  )}
                </div>
                <div className="fb-item fb-map" style={{ width: '100vw', height: '80vh' }}>
                  <LeafletMap zoom={4} weather={true} height='50vh' />
                </div>
              </div>
            </Grid.Column>
            <Grid.Column xs={1} />
          </Grid.Row>
        </Grid>
      </div>

      <div className="login_photo_container">
        <img className="login_photo" src='/Images/login_picture.jpg' alt="Login" />
      </div>

      <Modal size={'mini'} open={isLoginError} dimmer={'blurring'} onClose={() => setIsLoginError(false)} closeIcon>
        <Modal.Header>Error Logging In</Modal.Header>
        <Modal.Content>{loginError}</Modal.Content>
      </Modal>

      {config.featureFlags.ssoLogin && (
        <SSOLoginModal isOpen={isSSOModalOpen} onClose={() => setIsSSOModalOpen(false)} />
      )}
    </div>
  );
};

export default Login;
